<template>
  <div>
    <v-toolbar flat color="white">
      <v-toolbar-title class="table_title">
        {{ $t("trucks_crud.manage_trucks") }}
      </v-toolbar-title>
      <v-divider class="mx-2" inset vertical></v-divider>
      <v-text-field
        v-model="filter"
        append-icon="search"
        :label="$t('trucks_crud.search')"
        single-line
        hide-details
        clearable
        autofocus
        @input="isChangingFilter = true"
      ></v-text-field>

      <v-flex xs6 md2>
        <v-text-field
          prepend-icon="event"
          single-line
          readonly
          hide-details
          clearable
          v-model="deliveryDateFrom"
          :label="$t('trucks_crud.delivery_date_from')"
          @click="changeDate($event, 'deliveryDateFrom')"
        ></v-text-field>
      </v-flex>

      <v-flex xs6 md2>
        <v-text-field
          prepend-icon="event"
          single-line
          readonly
          hide-details
          clearable
          v-model="deliveryDateTo"
          :label="$t('trucks_crud.delivery_date_to')"
          @click="changeDate($event, 'deliveryDateTo')"
        ></v-text-field>
      </v-flex>

      <!-- status selector -->
      <status-selector
        :from="statusFrom"
        :to="statusTo"
        :table="'trucks'"
        :language="getLanguage"
        @statusesChanged="changeStatusFilters"
      />
      <!--  -->
      <v-spacer></v-spacer>
      <v-dialog v-model="dialog" max-width="70%">
        <v-card>
          <v-card-title>
            <span class="headline">{{ formTitle }}</span>
          </v-card-title>

          <v-card-text>
            <v-container grid-list-md>
              <v-layout wrap>
                <v-flex xs12 sm6 md4>
                  <v-text-field
                    ref="description"
                    v-model="editedItem.description"
                    :label="$t('trucks_crud.description')"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-menu
                    v-model="menu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="editedItem.delivery_date"
                        :label="$t('trucks_crud.delivery_date')"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="editedItem.delivery_date"
                      @input="menu = false"
                      :locale="getLocale"
                    ></v-date-picker>
                  </v-menu>
                </v-flex>
                <v-flex xs12 sm6 md4 v-if="editedIndex != -1">
                  <v-text-field
                    v-model="editedItem.status"
                    type="number"
                    min="0"
                    :label="$t('trucks_crud.status')"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4 v-if="editedIndex != -1">
                  <v-text-field
                    v-model="editedItem.trailer_number_plate"
                    type="text"
                    :label="$t('trucks_crud.vehicle')"
                  ></v-text-field>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" flat @click="close">
              {{ $t("trucks_crud.cancel") }}
            </v-btn>
            <v-btn
              color="blue darken-1"
              flat
              @click="edit"
              v-if="editedIndex != -1"
              >{{ $t("trucks_crud.edit") }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>
    <div class="table-wrapper">
      <div class="progress-wrapper">
        <v-progress-linear :indeterminate="loading"></v-progress-linear>
      </div>
      <div class="rTable">
        <div class="rTableRow problem-header">
          <div
            class="rTableHead"
            v-for="(header, i) in headers"
            :key="i"
            :style="{ width: header.width + '%' }"
          >
            {{ header.text }}
          </div>
        </div>
        <div class="rTableRow" v-for="(truck, i) in trucks" :key="i">
          <div class="rTableCell">
            <a @click="manageTruckPallets(truck)">
              {{ getDescription(truck.description) }}
            </a>
          </div>
          <div class="rTableCell">
            <a
              v-for="(order, index) in getOrderNames(truck.orders)"
              :key="index"
              @click="openOrder(truck, order)"
            >
              <span
                v-if="
                  getOrderNames(truck.orders).length != 1 &&
                  index != getOrderNames(truck.orders).length - 1
                "
                >{{ order }},</span
              >
              <span v-else>{{ order }}</span>
            </a>
          </div>
          <div class="rTableCell">{{ truck.customers }}</div>
          <div class="rTableCell">{{ truck.delivery_date }}</div>
          <div class="rTableCell">{{ truck.vehicle }}</div>
          <div class="rTableCell">{{ truck.pallets_count }}</div>
          <div class="rTableCell">{{ truck.products_count }}</div>
          <div class="rTableCell">
            <change-status-to
              @statusChanged="initialize"
              :id="truck.id"
              :statusNames="statusNames"
              :statusRights="statusRights"
              :user="user"
              :currentStatus="truck.status"
              :table="'trucks'"
            />
          </div>
          <div class="rTableCell">
            <v-icon
              v-if="truck.status >= 1 && user.type != 6"
              small
              class="mr-2 barcode-icon"
              v-tooltip="$t('trucks_crud.load_pallets')"
              @click="scanTruck(truck)"
              >line_weight</v-icon
            >
            <status-changes
              class="mr-2"
              :item="truck"
              :table="'truck'"
              v-tooltip="$t('trucks_crud.view_status_changes')"
            />
            <v-icon
              v-if="user.type != 6"
              small
              class="mr-2"
              @click="editItem(truck)"
              v-tooltip="$t('trucks_crud.edit')"
              >edit</v-icon
            >
            <v-icon
              v-if="user.type != 6"
              small
              class="mr-2"
              @click="deleteItem(truck)"
              v-tooltip="$t('trucks_crud.delete')"
              >delete</v-icon
            >
          </div>
        </div>
      </div>
    </div>
    <div class="text-xs-center pt-2">
      <v-pagination
        v-model="pagination.page"
        :length="pages"
        @input="initialize"
      ></v-pagination>
    </div>
    <DatePicker ref="datePicker" @dateSelected="selectDate" />
  </div>
</template>

<script>
import ChangeStatusTo from "@/components/status-changes/StatusChangeTo.vue";
import StatusChanges from "@/components/status-changes/StatusChanges.vue";
import StatusSelector from "@/components/status-changes/StatusSelector.vue";
import Truck from "@/models/Truck.js";
import Vehicle from "@/models/Vehicle.js";
import DatePicker from "@/components/DatePicker.vue";

export default {
  components: {
    StatusChanges,
    ChangeStatusTo,
    StatusSelector,
    DatePicker,
  },
  props: ["user"],
  data() {
    return {
      isChangingFilter: false,
      filterTimer: 0,
      loading: false,
      menu: false,
      deliveryDateFromMenu: false,
      deliveryDateToMenu: false,
      modal: false,
      dialog: false,
      filter: this.getFilterFromRoute(this.$route.params.filter),
      headers: getHeaders(this),
      trucks: [],
      statusNames: this.$session.get("statusNames").filter((e) => {
        return e.table_name == "trucks";
      }),
      statusRights: this.$session.get("statusRights"),
      editedIndex: -1,
      pagination: {
        rowsPerPage: 50, // -1 for All
        descending: this.getOrderFromRoute(this.$route.params.order),
        sortBy: this.getOrderByFromRoute(this.$route.params.order_by),
        page:
          this.$route.params.page != null
            ? parseInt(this.$route.params.page)
            : 1,
      },
      rowsPerPageItems: getRowsPerPageItems(this),
      editedItem: {
        description: "",
        delivery_date: new Date().toISOString().substr(0, 10),
        status: 0,
        trailer_number_plate: "",
      },
      defaultItem: {
        description: "",
        delivery_date: new Date().toISOString().substr(0, 10),
        status: 0,
        trailer_number_plate: "",
      },
      pages: 1,
      deliveryDateFrom: this.getDeliveryDateFrom(
        this.$route.params.delivery_date_from
      ),
      deliveryDateTo: this.getDeliveryDateTo(
        this.$route.params.delivery_date_to
      ),
      statusFrom:
        this.$route.params.status_from != null
          ? parseInt(this.$route.params.status_from)
          : 1,
      statusTo:
        this.$route.params.status_to != null
          ? parseInt(this.$route.params.status_to)
          : 100,
      onKeyUp: (e) => {
        if (e.key === "Escape") {
          this.dialog = false;
        }
        if (e.key === "Enter") {
          if (this.dialog) {
            if (this.editedIndex != -1) {
              this.edit();
            }
          }
        }
      },
    };
  },

  computed: {
    formTitle() {
      return this.editedIndex === -1
        ? this.$t("trucks_crud.new_truck")
        : this.$t("trucks_crud.edit_truck");
    },

    getLanguage() {
      return this.$router.currentRoute.params.lang.toUpperCase();
    },

    getLocale() {
      switch (this.$router.currentRoute.params.lang) {
        case "lt":
          return "lt-LT";
        case "en":
          return "en-GB";
        default:
          return "en-GB";
      }
    },
  },

  watch: {
    dialog(val) {
      if (val) {
        this.keyUpHandler = this.onKeyUp.bind(this);
        document.addEventListener("keyup", this.keyUpHandler);
        if (this.editedIndex == -1) {
          if (!this.user.hasRights("TP_EDIT")) {
            alert(this.$t("trucks_crud.has_no_right_to_create_new_truck"));
            setTimeout(() => {
              this.close();
            }, 10);
            return 0;
          }
        }
        setTimeout(() => {
          this.$refs.description.focus();
        }, 20);
      } else {
        document.removeEventListener("keyup", this.keyUpHandler);
        this.close();
      }
    },
    isChangingFilter(val) {
      if (!val) {
        this.search();
      }
    },
    filter() {
      clearTimeout(this.filterTimer);
      this.filterTimer = setTimeout(() => {
        this.isChangingFilter = false;
      }, 500);
      this.redirect();
    },
    deliveryDateFrom(val) {
      this.redirect();
      this.deliveryDateTo = val;
      this.initialize();
    },
    deliveryDateTo(val) {
      this.redirect();
      if (val < this.deliveryDateFrom) {
        this.deliveryDateFrom = val;
      }
      this.initialize();
    },
    statusFrom() {
      this.redirect();
      this.initialize();
    },
    statusTo() {
      this.redirect();
      this.initialize();
    },
    pagination: {
      handler() {
        this.redirect();
        this.initialize();
      },
      deep: true,
    },
  },

  created() {
    this.initialize();
  },

  methods: {
    async initialize() {
      this.loading = true;
      const { trucks, total, page, pages } = await Truck.loadTrucks(
        this.getParams()
      );
      if (total != 0) {
        this.pagination.page = page;
        this.pagination.totalItems = total;
        this.pages = pages;
      } else {
        this.pagination.page = 1;
        this.pagination.totalItems = 0;
        this.pages = 1;
      }
      this.trucks = trucks;
      this.loading = false;
    },

    /**
     * gets description string
     */
    getDescription(description) {
      if (description != null) {
        return description;
      }
      return "---";
    },

    /**
     * opens truck scanning window
     */
    scanTruck(truck) {
      this.$router
        .push({
          path: `/${this.$router.currentRoute.params.lang}/truck-scanner/${truck.id}`,
        })
        .catch(() => {});
    },

    /**
     * gets seperate order names from order string
     */
    getOrderNames(orderString) {
      if (orderString == null) {
        return [];
      }
      const orderNames = orderString.split(",");
      return orderNames;
    },

    /**
     * opens order page with products
     */
    async openOrder(truck, orderCode) {
      let order = null;
      truck = await Truck.loadTruck(truck.id);
      truck.pallets.forEach((pallet) => {
        pallet.products.forEach((product) => {
          if (product.order.toString() == orderCode.trim()) {
            order = product.order;
          }
        });
      });
      if (order) {
        this.$router
          .push({
            path: `/${this.$router.currentRoute.params.lang}/products/${order.id}`,
          })
          .catch(() => {});
      }
    },

    /**
     * gets filter from router
     * @param filter
     */
    getFilterFromRoute(filter) {
      if (typeof filter == "string" && filter == "null") {
        return null;
      }
      if (filter == null) {
        return null;
      }
      return filter;
    },

    /**
     * gets order by from router
     * @param orderBy
     */
    getOrderByFromRoute(orderBy) {
      if (typeof orderBy == "string" && orderBy == "null") {
        return null;
      }
      if (orderBy == null) {
        return null;
      }
      return orderBy;
    },

    /**
     * gets order from router
     * @param order
     */
    getOrderFromRoute(order) {
      if (typeof order == "string" && order == "null") {
        return true;
      }
      if (order == null) {
        return true;
      }
      return order;
    },

    /**
     * gets delivery date from from router
     * @param deliveryDateFrom
     */
    getDeliveryDateFrom(deliveryDateFrom) {
      if (typeof deliveryDateFrom == "string" && deliveryDateFrom == "null") {
        return null;
      }
      if (deliveryDateFrom == null) {
        return null;
      }
      return deliveryDateFrom;
    },

    /**
     * gets delivery date to from router
     * @param deliveryDateTo
     */
    getDeliveryDateTo(deliveryDateTo) {
      if (typeof deliveryDateTo == "string" && deliveryDateTo == "null") {
        return null;
      }
      if (deliveryDateTo == null) {
        return null;
      }
      return deliveryDateTo;
    },

    /**
     * change status filters
     * @param statuses
     */
    changeStatusFilters(statuses) {
      this.statusFrom = statuses.status_from != null ? statuses.status_from : 0;
      this.statusTo = statuses.status_to != null ? statuses.status_to : 100;
      if (this.filter == "") {
        this.filter = null;
      }
    },

    editItem(item) {
      this.editedIndex = this.trucks.indexOf(item);
      this.editedItem = Object.assign({}, item);
      if (item.vehicle) {
        this.editedItem.trailer_number_plate =
          item.vehicle.trailer_number_plate;
      }
      this.dialog = true;
    },

    deleteItem(item) {
      confirm(this.$t("trucks_crud.confirm_delete")) && this.delete(item);
    },

    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      }, 300);
    },

    async edit() {
      const truck = new Truck(this.editedItem);
      if (this.editedItem.trailer_number_plate != "") {
        if (!this.editedItem.vehicle.id) {
          const vehicle = await Vehicle.create({
            truck_id: truck.id,
            trailer_number_plate: this.editedItem.trailer_number_plate,
          });
          truck.vehicle_id = vehicle.id;
        } else {
          this.editedItem.vehicle.trailer_number_plate = this.editedItem.trailer_number_plate;
          this.editedItem.vehicle.save();
        }
      }
      await truck.save();
      this.initialize();
      this.close();
    },

    async delete(truck) {
      await truck.remove();
      this.initialize();
    },

    search() {
      this.page = 1;
      this.initialize();
    },

    reset() {
      this.$router
        .push({
          path: `/${this.$router.currentRoute.params.lang}/trucks-list`,
        })
        .catch(() => {});
      this.$router.go();
    },

    filterByDate() {
      this.deliveryDateFromMenu = false;
      this.deliveryDateToMenu = false;
    },

    prepareData(truck) {
      let data = {};
      truck.delivery_date != null
        ? (data["delivery_date"] = truck.delivery_date)
        : null;
      truck.status != null ? (data["status"] = truck.status) : null;
      truck.description != null
        ? (data["description"] = truck.description)
        : null;
      return data;
    },

    getParams() {
      let params = {
        page: this.pagination.page,
        rows: this.pagination.rowsPerPage,
      };
      this.pagination.sortBy != null
        ? (params["order_by"] = this.pagination.sortBy)
        : null;
      this.filter != null && this.filter.length != 0
        ? (params["filter"] = this.filter)
        : null;
      this.deliveryDateFrom != null
        ? (params["delivery_date_from"] = this.deliveryDateFrom)
        : null;
      this.deliveryDateTo != null
        ? (params["delivery_date_to"] = this.deliveryDateTo)
        : null;
      this.statusFrom != null && this.statusFrom.length != 0
        ? (params["status_from"] = this.statusFrom)
        : null;
      this.statusTo != null && this.statusTo.length != 0
        ? (params["status_to"] = this.statusTo)
        : null;
      this.pagination.descending != null
        ? (params["order"] = this.pagination.descending ? "desc" : "asc")
        : null;
      return params;
    },

    async manageTruckPallets(truck) {
      if (truck.orders != "") {
        this.$router.push({
          name: "pallet-planning",
          params: { filter: truck.orders },
        });
      }
    },

    changeDate(event, key) {
      if (this.$refs.datePicker) {
        this.$refs.datePicker.open(event, key);
      }
    },

    selectDate(date, key) {
      this[key] = date;
    },

    /**
     * gets link where to redirect
     */
    redirect() {
      const filter =
        this.filter != null && this.filter.length > 0 ? this.filter : null;
      const deliveryDateFrom =
        this.deliveryDateFrom != null ? this.deliveryDateFrom : null;
      const deliveryDateTo =
        this.deliveryDateTo != null ? this.deliveryDateTo : null;
      const orderBy =
        this.pagination.sortBy == null ? "null" : this.pagination.sortBy;
      const order =
        this.pagination.descending == null
          ? "null"
          : this.pagination.descending;
      const page = this.pagination.page != null ? this.pagination.page : null;
      this.$router
        .replace({
          path: `/${this.$router.currentRoute.params.lang}/trucks-list/${filter}/${deliveryDateFrom}/${deliveryDateTo}/${this.statusFrom}/${this.statusTo}/${page}/${orderBy}/${order}`,
        })
        .catch(() => {});
    },
  },
};

function getRowsPerPageItems(vue) {
  return [5, 10, 25, { text: vue.$t("trucks_crud.all"), value: -1 }];
}

function getHeaders(vue) {
  return [
    {
      text: vue.$t("trucks_crud.description"),
      value: "description",
      align: "left",
      width: 5,
    },
    {
      text: vue.$t("trucks_crud.orders"),
      value: "orders",
      align: "left",
      width: 20,
    },
    {
      text: vue.$t("trucks_crud.customers"),
      value: "customers",
      align: "left",
      width: 25,
    },
    {
      text: vue.$t("trucks_crud.delivery_date"),
      value: "delivery_date",
      align: "left",
      width: 5,
    },
    {
      text: vue.$t("trucks_crud.vehicle"),
      value: "vehicle",
      align: "left",
      sortable: false,
      width: 15,
    },
    {
      text: vue.$t("trucks_crud.pallets_count"),
      value: "pallets_count",
      align: "left",
      sortable: false,
      width: 5,
    },
    {
      text: vue.$t("trucks_crud.products_count"),
      value: "products_count",
      align: "left",
      sortable: false,
      width: 5,
    },
    {
      text: vue.$t("trucks_crud.status"),
      value: "status",
      align: "left",
      width: 10,
    },
    {
      text: vue.$t("trucks_crud.actions"),
      value: "actions",
      sortable: false,
      align: "left",
      width: 10,
    },
  ];
}
</script>

<style scoped>
.table_title {
  width: 10%;
}
span:hover {
  color: rgb(25, 118, 210);
}
.barcode-icon {
  -webkit-transform: rotate(90deg);
  -moz-transform: rotate(90deg);
  -o-transform: rotate(90deg);
  -ms-transform: rotate(90deg);
  transform: rotate(90deg);
}
table.v-table tbody td,
table.v-table tbody th {
  height: 10px;
}
</style>
